import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { authClient } from "./auth.js";
import { Logo } from "./ui/logo.js";

/**
 * Sign-in (docs/12, 06 step 7). Two panels like the old client: the form on the left, the
 * product's story on the right. A magic link by email, Google when the server has it configured.
 * Nothing else — no password, no sign-up form.
 */
/**
 * Where a sign-in comes back to: the address the person arrived at. Normally `/`; from the export email it is
 * `/?export=<id>`, and landing on `/` instead would lose the one thing they came for (ADR 0019).
 */
// Leading slashes collapsed: an address like `//evil.example/` is a path to this app but, handed on as the place to
// come back to, a protocol-relative URL to somewhere else.
const comeBack = () => `${location.pathname.replace(/^\/+/, "/")}${location.search}`;

export function Login() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cfg = useQuery({ queryKey: ["auth-config"], queryFn: async () => (await fetch("/v1/auth/config")).json() as Promise<{ google: boolean }> });

  async function send(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const r = await authClient.signIn.magicLink({ email: email.trim(), callbackURL: comeBack() });
      if (r.error) setError(r.error.message ?? "The link could not be sent. Try again in a moment.");
      else setSent(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="h-full flex bg-panel">
      <div className="w-full lg:w-[42%] xl:w-[37%] flex flex-col px-6 py-8 sm:px-10 overflow-y-auto">
        <div className="flex-1 grid place-items-center">
          <div className="w-full max-w-[380px] flex flex-col gap-7">
            <header className="flex flex-col items-center text-center gap-1">
              <a href="https://joogoos.com" className="flex flex-col items-center gap-1 group">
                <Logo size={56} />
                <h1 className="mt-1 text-2xl font-extrabold tracking-tight group-hover:text-coral transition-colors">joogoos</h1>
              </a>
              <h2 className="text-base font-semibold text-coral">Sign in</h2>
              <p className="text-sm text-muted">A link in your email. No password to remember.</p>
            </header>

            {sent ? (
              <div className="flex flex-col gap-3 text-center">
                <div className="rounded-[10px] bg-tint px-4 py-5">
                  <p className="text-sm font-semibold">Check your inbox.</p>
                  <p className="text-[13px] text-muted mt-1 leading-relaxed">
                    If <b className="text-ink">{email.trim()}</b> can sign in, a link is on its way. It works once and expires in 15 minutes.
                    After five links to one address in 15 minutes, no more are sent for a while.
                  </p>
                </div>
                <button onClick={() => { setSent(false); setError(null); }} className="text-[13px] text-muted hover:text-ink underline underline-offset-2">
                  Use a different address
                </button>
              </div>
            ) : (
              <form onSubmit={send} className="flex flex-col gap-4">
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium text-ink-2">Email</span>
                  <input
                    type="email"
                    required
                    autoFocus
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@business.com"
                    className="border border-line-2 rounded-lg px-3.5 py-2.5 text-sm bg-panel outline-none focus:border-ink/40"
                  />
                </label>
                <button disabled={busy} className="py-3 rounded-lg bg-coral text-white text-sm font-semibold hover:bg-coral-2 disabled:opacity-50">
                  {busy ? "Sending…" : "Send me a link"}
                </button>

                {cfg.data?.google ? (
                  <>
                    <div className="flex items-center gap-3 text-xs text-faint">
                      <span className="flex-1 h-px bg-line" />or<span className="flex-1 h-px bg-line" />
                    </div>
                    <button
                      type="button"
                      onClick={() => authClient.signIn.social({ provider: "google", callbackURL: comeBack() })}
                      className="flex items-center justify-center gap-2.5 py-3 rounded-lg border-2 border-line bg-panel text-sm font-medium hover:bg-page"
                    >
                      <GoogleMark /> Continue with Google
                    </button>
                  </>
                ) : null}

                {error ? <p className="text-bad text-xs">{error}</p> : null}
              </form>
            )}

            <p className="text-xs text-faint text-center leading-relaxed">
              New here? The link creates your workspace. Joogoos is paid for as it is used, from $10 of credit.
              <br />
              {/* Terms and privacy are pages on the marketing site, not routes in this app: asking for
                  them here only hits the single-page fallback and lands the reader back on this form. */}
              <a href="https://joogoos.com" className="text-muted hover:text-ink">joogoos.com</a> ·{" "}
              <a href="https://joogoos.com/terms" className="text-muted hover:text-ink">Terms</a> ·{" "}
              <a href="https://joogoos.com/privacy" className="text-muted hover:text-ink">Privacy</a>
            </p>
          </div>
        </div>
      </div>

      <Showcase />
    </div>
  );
}

/** The right panel: what the product does, in one line and one picture. Hidden below lg. */
function Showcase() {
  return (
    <div className="relative hidden lg:flex flex-1 overflow-hidden bg-gradient-to-br from-page via-panel to-tint">
      <div className="w-full flex flex-col justify-center px-10">
        <div className="max-w-[560px] flex flex-col gap-2">
          <h2 className="font-display text-[34px] font-bold tracking-[-.035em] leading-[1.04]">Your photos in.<br />A post or a short out.</h2>
          <p className="text-sm text-muted max-w-md">
            One idea and the pictures you already have. The plan shows its price before anything is made, and
            nothing posts unless you say so.
          </p>
          <Flow />
        </div>
      </div>
    </div>
  );
}

/** The pipeline as a picture: your photos, the steps, the video. Drawn, so it needs no assets. */
function Flow() {
  const step = (x: number, y: number, label: string) => (
    <g key={label} transform={`translate(${x} ${y})`}>
      <rect width="96" height="34" rx="8" fill="#ffffff" stroke="#e8e4df" />
      <text x="48" y="22" textAnchor="middle" fontSize="11" fill="#3f3a34" fontWeight="600">{label}</text>
    </g>
  );
  return (
    <svg viewBox="0 0 520 250" className="mt-7 w-full max-w-[520px]" role="img" aria-label="photos become a script, shots, voice and a rendered video">
      <rect x="0" y="60" width="86" height="120" rx="10" fill="#c9a27a" />
      <rect x="10" y="72" width="86" height="120" rx="10" fill="#8a6d4b" opacity=".85" />
      <rect x="20" y="84" width="86" height="120" rx="10" fill="#b56b45" />
      <path d="M118 138 C 150 138, 150 60, 182 60" fill="none" stroke="#d5d0c9" strokeWidth="1.5" />
      <path d="M118 138 C 150 138, 150 122, 182 122" fill="none" stroke="#d5d0c9" strokeWidth="1.5" />
      <path d="M118 138 C 150 138, 150 184, 182 184" fill="none" stroke="#d5d0c9" strokeWidth="1.5" />
      {[step(182, 43, "script"), step(182, 105, "voice"), step(182, 167, "music")]}
      <path d="M278 60 C 312 60, 312 138, 346 138" fill="none" stroke="#d5d0c9" strokeWidth="1.5" />
      <path d="M278 122 C 312 122, 312 138, 346 138" fill="none" stroke="#d5d0c9" strokeWidth="1.5" />
      <path d="M278 184 C 312 184, 312 138, 346 138" fill="none" stroke="#d5d0c9" strokeWidth="1.5" />
      <rect x="346" y="48" width="102" height="180" rx="10" fill="#17171a" />
      <rect x="356" y="58" width="82" height="160" rx="6" fill="#5f7f8f" />
      <circle cx="397" cy="138" r="17" fill="#ffffff" opacity=".92" />
      <path d="M392 130 l12 8 -12 8 z" fill="#17171a" />
      <text x="397" y="243" textAnchor="middle" fontSize="11" fill="#7a7269" fontWeight="600">15 seconds, priced first</text>
    </svg>
  );
}

function GoogleMark() {
  return (
    <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.2 17.6 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.6-.15-3.15-.42-4.65H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.6 5.9c4.44-4.1 7.22-10.16 7.22-17.45z" />
      <path fill="#FBBC05" d="M10.4 28.7a14.6 14.6 0 0 1 0-9.4l-7.8-6.1a24 24 0 0 0 0 21.6l7.8-6.1z" />
      <path fill="#34A853" d="M24 48c6.5 0 11.95-2.15 15.93-5.85l-7.6-5.9c-2.1 1.42-4.82 2.25-8.33 2.25-6.4 0-11.7-3.7-13.6-9.8l-7.8 6.1C6.5 42.6 14.6 48 24 48z" />
    </svg>
  );
}
